import React, { FC, memo } from "react";
import Select, { components, MenuPlacement } from "react-select";

import { mergeReactSelectSelectedFirst } from "~/shared/lib/selectedFirstOptions";

import s from "~/shared/ui/Fields/Select/Select.less";

interface IProps {
  id?: string;
  onChange?;
  value?;
  defaultValue?;
  isLoading?: boolean;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  options?: object[];
  onScrollBottom?: () => void;
  getSearchText?: (e) => void;
  menuPlacement?: MenuPlacement;
  menuPortalTarget?: HTMLElement | null;
  menuPosition?: "fixed" | "absolute";
  custom?: boolean;
  menuCustom?: boolean;
  isHeight?: boolean;
  newStyle?: boolean;
  isControlPadding?: boolean;
  menuListHeight?: string;
  onSelectDecorator?: () => void;
  newCustomClassNames?: any;
  placeholder?: string;
  isClearable?: boolean;
}

const CustomSelect: FC<IProps> = ({
  id,
  onChange,
  value,
  defaultValue,
  error,
  isLoading = false,
  disabled = false,
  required = false,
  custom = false,
  isHeight = false,
  isControlPadding = false,
  newStyle = false,
  menuCustom = false,
  options = [],
  onScrollBottom,
  getSearchText,
  menuPlacement = "bottom",
  menuPortalTarget,
  menuPosition,
  menuListHeight,
  onSelectDecorator = () => {},
  newCustomClassNames,
  placeholder = null,
  isClearable = false,
}) => {
  const { Option } = components;
  const IconOption = (props) => {
    return (
      <Option {...props}>
        {props.data.icon && props.data.icon}
        {props.data.label}
      </Option>
    );
  };

  const customClassNamesOld = {
    container: (provided) => ({
      ...provided,
      width: "100%",
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: "15px",
      width: "100%",
      height: isHeight || newStyle ? "auto" : "25px",
      borderRadius: "6px",
      border: "none",
      padding: newStyle && "6px 0px",
      fontSize: newStyle ? "12px" : "10px",
      fontFamily: "LADA_Pragmatica",
      webkitBoxShadow: state.isFocused
        ? "0 0 3px rgb(255 158 27)"
        : "0 1px 3px rgba(16, 15, 14, 0.25)",
      boxShadow: state.isFocused
        ? "0 0 3px rgb(255 158 27)"
        : error && value?.value === null
        ? "0 1px 3px rgba(255, 0, 0, 0.75)"
        : "0 1px 3px rgba(16, 15, 14, 0.25)",
      cursor: "pointer",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: isHeight ? "auto" : "22px",
    }),
    menu: menuCustom
      ? (provided) => ({
          ...provided,
          width: "100%",
          zIndex: 2,
        })
      : (provided) => ({
          ...provided,
          zIndex: 2,
        }),
    menuList: menuListHeight
      ? (provided) => ({
          ...provided,
          maxHeight: menuListHeight || "none",
        })
      : menuCustom
      ? (provided) => ({
          ...provided,
          width: "100%",
        })
      : (provided) => ({
          ...provided,
        }),
    option: (provided, state) => {
      return {
        ...provided,
        color:
          state.isFocused && !state.isDisabled
            ? "white"
            : state.isDisabled
            ? "#dadee0"
            : "#65727c",
        fontSize: "11px",
        background: state.isDisabled && state.isFocused ? "#FFFFFF" : null,
      };
    },
    valueContainer: (provided) => ({
      ...provided,
      padding: isControlPadding ? "10px 12px" : "2px 8px",
      alignItems: "flex-start",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: "none",
    }),
    singleValue: (provided) => {
      return {
        ...provided,
        color: newStyle ? "#28333A" : "#000",
      };
    },
  };

  const customClassNames = {
    container: (provided, state) => {
      return {
        ...provided,
        width: "100%",
        borderRadius: "5px",
        border:
          state.isHover || state.isFocused
            ? "1px solid #e37639"
            : "1px solid #C1C3C5",
        outline: "none",

        "&:hover": {
          border: "1px solid #e37639",
          cursor: "pointer",

          svg: {
            stroke: "#e37639",
            patch: {
              stroke: "#e37639",
            },
          },
        },
      };
    },
    control: (provided, state) => ({
      ...provided,
      fontSize: "12px",
      fontFamily: "LADA_Pragmatica",
      minHeight: "24px",
      color: "#BDC2C5",
      border: state.isHover || state.isFocused ? "none" : "none",
      outline: state.isHover || state.isFocused ? "none" : "none",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: "none",
    }),
    menu: (provided, state) => ({
      ...provided,
      zIndex: 300,
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      minHeight: "24px",
      maxWidth: "60px",
      minWidth: "24px",
      padding: "0px",
    }),
    input: (provided, state) => ({
      ...provided,
      height: "28px",
      input: { height: "28px" },
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: "12px",
    }),
    indicatorContainer: (provided) => ({
      ...provided,
      padding: "0px",
      border: "none",
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      padding: "0px",
      color:
        state.isHover || state.isFocused || state.isActive
          ? "#e37639"
          : "#848E98",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#000000",
    }),
    placeholder: (provided) => ({
      ...provided,
    }),
  };

  const customTheme = (theme) => ({
    ...theme,
    colors: {
      ...theme.colors,
      primary25: "#f17830",
      primary: "#ff9e1b",
      danger: "#a64921",
      dangerLight: "#f5dcba",
    },
  });

  const sortedOptions = mergeReactSelectSelectedFirst(
    options as { value?: React.Key }[],
    value,
  );

  return (
    <>
      <Select
        id={id}
        placeholder={placeholder ? placeholder : ""}
        theme={customTheme}
        styles={{
          ...(!!newStyle ? customClassNames : customClassNamesOld),
          ...(menuPortalTarget
            ? { menuPortal: (base) => ({ ...base, zIndex: 9999 }) }
            : {}),
        }}
        components={
          newCustomClassNames
            ? null
            : {
                Option: IconOption,
              }
        }
        value={value}
        defaultValue={defaultValue}
        isClearable={isClearable}
        onChange={(e) => {
          onSelectDecorator();
          onChange(e);
        }}
        options={sortedOptions}
        isDisabled={disabled}
        isLoading={isLoading}
        onMenuScrollToBottom={onScrollBottom}
        onInputChange={getSearchText}
        menuPlacement={menuPlacement}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPosition}
        closeMenuOnScroll={
          menuPortalTarget
            ? (e: Event) => {
                const target = e.target as HTMLElement;
                return !target?.closest?.("[class*='menu']");
              }
            : undefined
        }
      />
      {error && <span className={s.error_text}>{error}</span>}
    </>
  );
};
export default memo(CustomSelect);
